import {
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LinearTagChip } from "components/LinearTagChip";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { useLinearTags } from "hooks/useLinearTags";
import {
  ActivityStatus,
  ActivityType,
  ExecutionPlanType,
  JobStatus,
  TierIiReportKind,
  WorkflowActivitiesQuery,
  WorkflowActivitiesQueryVariables,
  WorkflowType,
  WorkflowStatus,
} from "../../../../generated-graphql/graphql";
import { gql } from "../../../../generated-graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { BulkActionsMenu } from "./BulkActionsMenu";
import { ExportGridToCsvButton } from "../AssignmentManagerTable/ExportGridToCsvButton";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { WorkflowActivitiesRow } from "./types";
import { prettyPrintDate } from "util/dates";
import { WorkflowOverviewStats } from "../WorkflowOverviewStats/WorkflowOverviewStats";
import { useStats } from "../StatsContext";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  FilterGroup,
  parseOmnisearch,
  stringifyOmnisearch,
} from "components/Omnisearch/FilterPopover";

export const WORKFLOW_ACTIVITIES_QUERY = gql(`
    query WorkflowActivities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
        workflowActivities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
            items {
                id
                activityId
                workflowId
                cohortId
                tierIIReportId
                activityType
                activityStatus
                name
                reportingYear
                reportKind
                organizationName
                tenantId
                assigneeEmail
                credentials {
                    username
                    verificationStatus
                }
                activityCreatedAt
                errorMessage
                plan
                tags
                jobStatus
                description
                partnerTenantName
                cohortReadyMemberCount
                cohortTotalMemberCount
                cohortType
                workflowType
                facilityAddress
                workflowStatus
                state
                county
                fulfillmentNote
                csmName
                csmEmail
                amName
                amEmail
                dataManagerName
                dataManagerEmail
                wasteManagerName
                wasteManagerEmail
                pocName
                pocEmail
            }
            count
        }
    }
    `);

const persistenceKey = "workflow-activities";

export function WorkflowActivitiesTable() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const theme = useTheme();
  const { statsOpen } = useStats();
  const omnisearchInputRef = useRef<HTMLInputElement>(null);

  const { omnisearch, sortModel, setOmnisearch, getOmnisearchValue } =
    useOmnisearchDatagrid({
      persistenceKey,
      isURLDriven: true,
    });

  const [selectedRows, setSelectedRows] = useState<WorkflowActivitiesRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<
    Array<WorkflowActivitiesRow["id"]>
  >([]);

  // Fetch workflow activities data to collect all tags for Linear integration
  const { data: workflowActivitiesData } = useQuery<
    WorkflowActivitiesQuery,
    WorkflowActivitiesQueryVariables
  >(WORKFLOW_ACTIVITIES_QUERY, {
    // Keep this aligned with the datagrid query shape so we reliably get results (and cache hits).
    // We only need enough rows to cover what's visible + some buffer for Linear tag lookups.
    variables: {
      search: omnisearch,
      page: 0,
      pageSize: 200,
      sort: [...sortModel],
    },
    fetchPolicy: "cache-and-network",
  });

  // Collect all tags from all workflow activities for Linear issue lookup
  const allTagsForLinear = useMemo(() => {
    const tagSet = new Set<string>();
    const items = workflowActivitiesData?.workflowActivities?.items ?? [];
    items.forEach((row) => {
      row.tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet);
  }, [workflowActivitiesData]);

  // Get Linear issue statuses for all tags
  const { statusMap: linearStatusMap } = useLinearTags(allTagsForLinear);

  // Track omnisearch changes to handle scrolling from UnverifiedTable
  const prevOmnisearch = useRef<string>(omnisearch);

  // Scroll to omnisearch when navigation from UnverifiedTable is detected
  useEffect(() => {
    if (prevOmnisearch.current !== omnisearch && omnisearch) {
      // Check for UnverifiedTable navigation (State/Organization + Unverified)
      const hasUnverifiedFilter =
        omnisearch.includes("Unverified:true") ||
        omnisearch.toLowerCase().includes("unverified");
      const hasStateOrOrgFilter =
        omnisearch.includes("State:") ||
        omnisearch.includes("Organization:") ||
        omnisearch.toLowerCase().includes("state:") ||
        omnisearch.toLowerCase().includes("organization:");

      // If navigation from UnverifiedTable is detected
      if (hasUnverifiedFilter && hasStateOrOrgFilter) {
        if (omnisearchInputRef.current) {
          // Scroll to omnisearch input and focus it
          setTimeout(() => {
            if (omnisearchInputRef.current) {
              // Get the element's position
              const rect = omnisearchInputRef.current.getBoundingClientRect();
              const headerHeight = 80; // Approximate sticky header height

              // Scroll to position that accounts for the sticky header
              window.scrollTo({
                top: window.pageYOffset + rect.top - headerHeight,
                behavior: "smooth",
              });

              // Focus the search input so user can immediately start typing
              omnisearchInputRef.current.focus();
            }
          }, 300);
        }
      }
    }
    prevOmnisearch.current = omnisearch;
  }, [omnisearch]);

  const handleSelectedRowsChanged = useCallback(
    (rows: WorkflowActivitiesRow[]) => {
      setSelectedRows(rows);
      setSelectedRowIds(rows.map((row) => row.id));
    },
    []
  );

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
    setSelectedRowIds([]);
  }, []);

  // Check if "Assigned to me" filter is currently active
  const isAssignedToMeActive = useMemo(() => {
    if (!user?.email) return false;
    const filters = parseOmnisearch(omnisearch);
    return filters.assigneeEmail?.includes(user.email);
  }, [omnisearch, user?.email]);

  // Handle "Assigned to me" toggle
  const handleAssignedToMeToggle = useCallback(
    (checked: boolean) => {
      if (!user?.email) return;

      const currentFilters = parseOmnisearch(omnisearch);
      const reportKind = getOmnisearchValue("reportKind");

      if (checked) {
        // Add assigneeEmail filter
        const newFilters = {
          ...currentFilters,
          reportKind,
          assigneeEmail: user.email,
        };
        setOmnisearch(stringifyOmnisearch(newFilters));
      } else {
        // Remove assigneeEmail filter
        const { assigneeEmail, ...newFilters } = currentFilters;

        setOmnisearch(
          stringifyOmnisearch({
            ...newFilters,
            reportKind,
          })
        );
      }
    },
    [omnisearch, setOmnisearch, user?.email, getOmnisearchValue]
  );

  const columns: OmnisearchGridColDef<WorkflowActivitiesRow>[] = useMemo(
    () => [
      // === Organization/Facility columns ===
      {
        field: "organizationName",
        headerName: "Organization",
        flex: 0.2,
        minWidth: 120,
      },
      {
        field: "partnerTenantName",
        headerName: "Partner Tenant",
        flex: 1,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        renderCell: ({ row }) => {
          if (row.cohortId == null) {
            return (
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <Typography variant="body2">{row.name}</Typography>
                <Typography variant="caption">{row.facilityAddress}</Typography>
              </Box>
            );
          }
          return <Typography variant="body2">{row.name}</Typography>;
        },
      },
      {
        field: "state",
        headerName: "State",
        flex: 0.08,
        minWidth: 50,
      },
      {
        field: "county",
        headerName: "County",
        flex: 0.15,
        minWidth: 100,
        filterKeyType: "text",
      },

      // === Workflow columns ===
      {
        field: "workflowType",
        headerName: "Workflow Type",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(WorkflowType),
        renderCell: ({ row }) => {
          return prettyPrintEnumValue(row.workflowType);
        },
      },
      {
        field: "reportingYear",
        headerName: "Reporting Year",
        flex: 0.8,
      },
      {
        field: "reportKind",
        headerName: "Report Kind",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(TierIiReportKind),
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        flex: 1,
        enumValues: Object.values(WorkflowStatus),
        filterable: true,
        filterKeyType: "enumAutocomplete",
        renderCell: ({ row }) => {
          if (row.workflowStatus) {
            return (
              <Typography variant="body2">
                {prettyPrintEnumValue(row.workflowStatus)}
              </Typography>
            );
          }
          return "";
        },
      },

      // === Assignee column ===
      {
        field: "assigneeEmail",
        headerName: "Assignee Email",
        flex: 1.2,
        renderCell: ({ row }) => {
          if (row.assigneeEmail === "Unassigned") {
            return <Typography variant="body2"></Typography>;
          } else {
            return <Typography variant="body2">{row.assigneeEmail}</Typography>;
          }
        },
      },

      // === Activity columns (grouped) ===
      {
        field: "activityType",
        headerName: "Activity Type",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ActivityType),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.5,
      },
      {
        field: "activityStatus",
        headerName: "Activity Status",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ActivityStatus),
        renderCell: ({ row }) => {
          if (row.activityType) {
            if (row.activityStatus === ActivityStatus.Completed)
              return (
                <Typography variant="body2" color="success">
                  Completed
                </Typography>
              );
            else if (row.activityStatus === ActivityStatus.Error)
              return (
                <Typography variant="body2" color="error">
                  Error
                </Typography>
              );
            else return prettyPrintEnumValue(row.activityStatus);
          }
          return "N/A";
        },
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1.5,
      },
      {
        field: "activityCreatedAt",
        headerName: "Activity Created At",
        flex: 1,
        renderCell: ({ row }) => {
          if (row.activityCreatedAt) {
            return (
              <Typography variant="body2">
                {prettyPrintDate(row.activityCreatedAt)}
              </Typography>
            );
          }
          return "";
        },
      },
      {
        field: "plan",
        headerName: "Plan",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ExecutionPlanType),
      },
      {
        field: "jobStatus",
        headerName: "Job Status",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
        renderCell: ({ row }) => {
          if (row.jobStatus?.includes("FAILED"))
            return (
              <Typography variant="body2" color="error">
                {prettyPrintEnumValue(row.jobStatus)}
              </Typography>
            );
          else if (row.jobStatus === JobStatus.Succeeded)
            return (
              <Typography variant="body2" color="success">
                Succeeded
              </Typography>
            );
          else return prettyPrintEnumValue(row.jobStatus);
        },
      },

      // === Other columns ===
      {
        field: "fulfillmentNote",
        headerName: "Fulfillment Note",
        flex: 0.2,
        minWidth: 150,
        sortable: false,
        filterable: true,
        renderCell: ({ row }) => {
          return row.fulfillmentNote ? (
            <Tooltip
              title={row.fulfillmentNote}
              slotProps={{
                tooltip: { sx: { whiteSpace: "pre-wrap" } },
              }}
            >
              <Typography variant="body2" noWrap>
                {row.fulfillmentNote}
              </Typography>
            </Tooltip>
          ) : null;
        },
      },

      // === Assignment columns ===
      {
        field: "credentials",
        headerName: "Credentials",
        flex: 1,
        renderCell: ({ row }) => {
          if (!row.credentials || row.credentials.length === 0) return null;

          // For now, just show the first credential
          const credential = row.credentials[0];

          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{credential.username}</Typography>
              <Typography variant="caption">
                {prettyPrintEnumValue(credential.verificationStatus)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "tags",
        headerName: "Tags",
        filterKeyType: "workflowTags",
        flex: 1,
        renderCell: ({ row }) => {
          if (row.tags.length === 0) return null;

          // Prefer showing a Linear ticket tag (ENG-####) if present; otherwise show the first tags.
          // Keep the compact two-row UI, but don't hide Linear tags behind "+N more".
          const linearTags = row.tags.filter((t) => /^ENG-\d+$/i.test(t));
          const nonLinearTags = row.tags.filter((t) => !/^ENG-\d+$/i.test(t));
          const orderedTags = [...linearTags, ...nonLinearTags];

          const visibleTags = orderedTags.slice(0, 2);
          const overflowCount = orderedTags.length - visibleTags.length;
          const showSecondRow = visibleTags.length >= 2;

          return (
            <Box sx={{ py: 0.25 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.25,
                  mb: showSecondRow ? 0.25 : 0,
                }}
              >
                <LinearTagChip
                  tag={visibleTags[0]}
                  linearStatusMap={linearStatusMap}
                  size="small"
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
              </Box>
              {showSecondRow && (
                <Box sx={{ display: "flex", gap: 0.25, alignItems: "center" }}>
                  {overflowCount === 0 ? (
                    <LinearTagChip
                      tag={visibleTags[1]}
                      linearStatusMap={linearStatusMap}
                      size="small"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  ) : (
                    <>
                      <LinearTagChip
                        tag={visibleTags[1]}
                        linearStatusMap={linearStatusMap}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                      <Chip
                        label={`+${overflowCount} more`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        },
      },

      // === Account team columns ===
      {
        field: "csmName",
        headerName: "CSM",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.csmName && !row.csmEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{row.csmName}</Typography>
              <Typography variant="caption">{row.csmEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "amName",
        headerName: "Account Manager",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.amName && !row.amEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{row.amName}</Typography>
              <Typography variant="caption">{row.amEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "dataManagerName",
        headerName: "Data Manager",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.dataManagerName && !row.dataManagerEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{row.dataManagerName}</Typography>
              <Typography variant="caption">{row.dataManagerEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "wasteManagerName",
        headerName: "Waste Manager",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.wasteManagerName && !row.wasteManagerEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{row.wasteManagerName}</Typography>
              <Typography variant="caption">{row.wasteManagerEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "pocName",
        headerName: "Customer POC",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.pocName && !row.pocEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{row.pocName}</Typography>
              <Typography variant="caption">{row.pocEmail}</Typography>
            </Box>
          );
        },
      },

      // === Cohort columns ===
      {
        field: "cohortMemberCount",
        headerName: "Cohort Member Count",
        flex: 1,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.workflowType !== WorkflowType.Cohort) return <></>;

          return (
            <Tooltip
              title={`${row.cohortReadyMemberCount} cohort ${
                row.cohortReadyMemberCount === 1 ? "member" : "members"
              } ready out of ${
                row.cohortTotalMemberCount
              } total possible members.`}
              placement="top"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                {row.cohortReadyMemberCount} / {row.cohortTotalMemberCount}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [linearStatusMap]
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      // Column 1: Organization + Workflow
      {
        name: "🏢 Organization & Facility",
        fields: [
          "organizationName",
          "partnerTenantName",
          "tenant",
          "name",
          "state",
          "county",
        ],
      },
      {
        name: "⚙️ Workflow & Activity",
        fields: [
          "activityType",
          "activityStatus",
          "workflowType",
          "workflowStatus",
          "plan",
          "jobStatus",
        ],
      },
      // Column 2: Report + Tags + Account Team
      {
        name: "📋 Report",
        fields: ["reportingYear", "reportKind"],
      },
      {
        name: "🏷️ Tags & Assignment",
        fields: ["tags", "assigneeEmail", "credentials"],
      },
      {
        name: "👥 Account Team",
        fields: ["csmName", "amName", "dataManagerName", "wasteManagerName"],
      },
      // Column 3: Customer (Other Filters will follow)
      {
        name: "🤝 Customer",
        fields: ["pocName"],
      },
    ],
    []
  );

  return (
    <Box p={theme.spacing(2)}>
      <Collapse in={statsOpen}>
        <WorkflowOverviewStats />
      </Collapse>
      <Paper
        elevation={0}
        sx={{ display: "flex", flexDirection: "column", position: "relative" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          px={3}
          paddingTop={3}
          paddingBottom={theme.spacing(3)}
        >
          <Typography variant="body1" color={theme.palette.text.secondary}>
            Current Activities
          </Typography>
          <FormControlLabel
            sx={{ marginRight: "0px" }}
            control={
              <Switch
                checked={isAssignedToMeActive}
                onChange={(_, checked) => handleAssignedToMeToggle(checked)}
                disabled={!user?.email}
              />
            }
            label="Assigned to me"
          />
        </Box>

        <Box position="relative" pb={3} sx={{ height: "100vh" }}>
          <OmnisearchDataGrid<
            WorkflowActivitiesQuery,
            WorkflowActivitiesQueryVariables,
            WorkflowActivitiesRow
          >
            omnisearchInputRef={omnisearchInputRef}
            autoFocus={false}
            isURLDriven
            persistenceKey={persistenceKey}
            columns={columns}
            columnVisibilityModel={{
              fulfillmentNote: false,
              csmName: false,
              amName: false,
              dataManagerName: false,
              wasteManagerName: false,
              pocName: false,
            }}
            dataQuery={WORKFLOW_ACTIVITIES_QUERY}
            additionalFilterColumns={[
              {
                header: "Organization",
                key: "tenant",
                filterKeyType: "multipleTenant",
              },
            ]}
            filterGroups={filterGroups}
            wideLayout
            checkboxSelection
            disableRowSelectionOnClick
            getItems={(data) => data.workflowActivities.items}
            getCount={(data) => data.workflowActivities.count}
            onRowClick={({ row }) => {
              if (row.tierIIReportId)
                navigate(
                  `/staff/fulfillment/${row.tierIIReportId}/activities/${row.activityId}`
                );
              else if (row.cohortId) navigate(`/staff/cohorts/${row.cohortId}`);
            }}
            getRowLink={(row) => {
              if (row.tierIIReportId)
                return `/staff/fulfillment/r/${row.tierIIReportId}/activities/${row.activityId}`;
              else if (row.cohortId) return `/staff/cohorts/${row.cohortId}`;

              return null;
            }}
            onSelectedRowsChanged={handleSelectedRowsChanged}
            selectedIds={selectedRowIds}
            commandButtons={[
              <Stack
                key="command-buttons"
                id="command-buttons"
                sx={{
                  marginBottom: "1rem",
                  marginTop: "1rem",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                }}
                spacing={2}
                alignItems="center"
              >
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  marginTop={0}
                  sx={{ flex: { sm: 1, lg: 2 }, alignItems: "center" }}
                >
                  <Box sx={{ marginRight: 2 }}>
                    <ExportGridToCsvButton
                      search={omnisearch}
                      sort={[...sortModel]}
                      grid="workflowActivities"
                    />
                  </Box>
                  <BulkActionsMenu
                    selectedRows={selectedRows}
                    onClearSelection={handleClearSelection}
                  />
                </Stack>
              </Stack>,
            ]}
            gridSx={{
              height: "100%",
              minHeight: "400px",
              maxHeight: "calc(100vh - 280px)", // Account for search controls, filters, and padding
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
